import { useCallback, useEffect, useState } from "react"
import { IconAlertTriangle, IconCheck, IconLoader2, IconTrash } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import { statusLabel } from "@/app/booking-calendar/lib/bookingStatus"
import {
  ReviewDecision,
  getReviewDetail,
  getReviewQueue,
  recordReviewDecision,
  revealReviewSsn,
  type BookingReviewDetail,
  type BookingReviewItem,
  type ReviewDecisionValue,
} from "@/app/booking-calendar/api/reviewApi"
import { BookingSummary } from "@/app/booking-calendar/components/BookingDetailsPanel"
import { BookingNotesSection } from "@/app/booking-calendar/components/BookingNotesSection"
import { appointmentStartLabel, reviewBookingSummary } from "./reviewBookingSummary"
import {
  allowedDecisions,
  decodeFlags,
  reassignmentWriteOffLines,
  rescheduleFailureLines,
  revealFailureMessage,
  reviewReasonLabels,
} from "./reviewDecisions"
import { ExistingAppointmentCard } from "./ExistingAppointmentCard"
import { ProcessedReviewList } from "./ProcessedReviewList"

// ── Types ────────────────────────────────────────────────────────────
// The API serializes these enums as numbers. The review reasons' labels, a
// bitmask mirroring AmsBookingReviewReason in Cms.Services, live in
// reviewDecisions.ts (REVIEW_REASONS).

const MATCH_BANDS = ["No match", "Weak", "Probable", "Strong"] as const

const PROGRAMS = [
  { bit: 1, label: "PSE" },
  { bit: 2, label: "LIHEAP" },
] as const

// ── Component ────────────────────────────────────────────────────────

export default function BookingReviewPage() {
  const [items, setItems] = useState<BookingReviewItem[]>([])
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState<BookingReviewDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [comments, setComments] = useState("")
  const [saving, setSaving] = useState(false)
  // AB#841: the decided items instead of the open queue. Read-only; the open queue is untouched.
  const [showProcessed, setShowProcessed] = useState(false)

  // Component-local, and cleared whenever the dialog opens or closes. A revealed Social Security
  // number lives exactly as long as the staff member is looking at it: never in a toast, never in
  // the queue state, never anywhere that survives navigating away.
  const [revealedSsn, setRevealedSsn] = useState<string | null>(null)
  const [revealing, setRevealing] = useState(false)

  const loadQueue = useCallback(async () => {
    setLoading(true)
    try {
      setItems(await getReviewQueue())
    } catch (err) {
      console.error("Failed to load the booking review queue:", err)
      toast.error("Failed to load the booking review queue.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadQueue()
  }, [loadQueue])

  const revealSsn = async (id: string) => {
    setRevealing(true)
    try {
      const result = await revealReviewSsn(id)
      setRevealedSsn(result.ssn)
    } catch (err) {
      console.error("Failed to reveal the Social Security number:", err)
      // AB#797: the reveal needs only this page's permission (AB#845), so say what actually happened.
      toast.error(revealFailureMessage(err))
    } finally {
      setRevealing(false)
    }
  }

  const openDetail = async (id: string) => {
    setDetailLoading(true)
    setComments("")
    setRevealedSsn(null)
    try {
      setDetail(await getReviewDetail(id))
    } catch (err) {
      console.error("Failed to load the booking request:", err)
      toast.error("Failed to load the booking request.")
    } finally {
      setDetailLoading(false)
    }
  }

  const decide = async (decision: ReviewDecisionValue) => {
    if (!detail) return
    setSaving(true)
    try {
      await recordReviewDecision(detail.item.id, decision, comments)
      toast.success(
        decision === ReviewDecision.CancelDuplicate
          ? "Appointment cancelled and recorded as a duplicate."
          : "Decision recorded."
      )
      setDetail(null)
      setRevealedSsn(null)
      await loadQueue()
    } catch (err) {
      console.error("Failed to record the decision:", err)
      toast.error("Failed to record the decision.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center gap-2">
        <IconAlertTriangle className="size-6" />
        <h1 className="text-2xl font-bold">Booking Review</h1>
      </div>
      <p className="text-muted-foreground max-w-3xl">
        Appointments that look like they belong to a household we have already seen this program
        year. Nothing here was refused at booking time and the caller was told nothing: these are
        flags for you to judge, and the only irreversible action is cancelling a duplicate.
      </p>

      <div className="flex items-center gap-2">
        <Checkbox
          id="review-show-processed"
          checked={showProcessed}
          onCheckedChange={(checked) => setShowProcessed(checked === true)}
        />
        <Label htmlFor="review-show-processed">Show processed</Label>
      </div>

      {showProcessed ? (
        <ProcessedReviewList />
      ) : loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-muted-foreground">
          Nothing needs review.
        </div>
      ) : (
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Appointment</TableHead>
                <TableHead>Client Name</TableHead>
                <TableHead>Intake Staff</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  {/* AB#796: the date and start time, then the confirmation number and location right under it. */}
                  <TableCell data-testid="review-appointment-cell">
                    <div className="font-medium">{appointmentStartLabel(item.slotStart) ?? "—"}</div>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-mono">{item.confirmationCode || "—"}</span> · {item.siteName ?? "—"}
                    </div>
                    <div className="text-sm text-muted-foreground">{statusLabel(item.status)}</div>
                  </TableCell>
                  <TableCell>{item.attendeeName || "—"}</TableCell>
                  <TableCell data-testid="review-intake-staff-cell">{item.hostName || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {reviewReasonLabels(item.reviewReasons).map((label) => (
                        <Badge key={label} variant="secondary">
                          {label}
                        </Badge>
                      ))}
                    </div>
                    {decodeFlags(item.blockedPrograms, PROGRAMS).length > 0 && (
                      <div className="text-sm text-muted-foreground mt-1">
                        Already served: {decodeFlags(item.blockedPrograms, PROGRAMS).join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.topMatchBand >= 3 ? "destructive" : "outline"}>
                      {MATCH_BANDS[item.topMatchBand] ?? item.topMatchBand}
                    </Badge>
                    <div className="text-sm text-muted-foreground mt-1">
                      {item.matchedFields ?? "—"}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => void openDetail(item.id)}>
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetail(null)
            // Closing the dialog is what discards a revealed number. Without this it would survive
            // into the next request the reviewer opened.
            setRevealedSsn(null)
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review appointment</DialogTitle>
            <DialogDescription>
              What the matcher saw. It compares normalized addresses, phone numbers and names; it
              does not use a score, because nothing has been calibrated yet.
            </DialogDescription>
          </DialogHeader>

          {detailLoading || !detail ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="flex flex-col gap-4">
              {/* AB#797: the product owner's order; the address and account number are rows when entered. */}
              <BookingSummary fields={reviewBookingSummary(detail)} />

              {/*
                A booking-move batch could not move this booking (AB#809). Staff fix it with the
                ordinary appointment actions; the queue only acknowledges it.
              */}
              {(() => {
                const failure = rescheduleFailureLines(detail)
                if (!failure) return null
                return (
                  <div className="rounded-lg border border-destructive/40 p-3 text-sm" data-testid="reschedule-failure">
                    <div className="font-medium">Reschedule failed</div>
                    <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                      <dt className="text-muted-foreground">Batch</dt>
                      <dd>{failure.batch}</dd>
                      <dt className="text-muted-foreground">Attempted target</dt>
                      <dd>{failure.target}</dd>
                      <dt className="text-muted-foreground">Failure</dt>
                      <dd>{failure.failure}</dd>
                    </dl>
                    <div className="mt-1 text-muted-foreground">
                      Use the appointment&apos;s own actions to fix it, then keep it here to acknowledge it.
                    </div>
                  </div>
                )
              })()}

              {/*
                The maintenance sweep wrote off a reassignment that had already cancelled this
                booking in Cal (ams-u3b). CMS still shows it as it was; staff rebook or cancel it with
                the ordinary appointment actions, and the queue only acknowledges it.
              */}
              {(() => {
                const writeOff = reassignmentWriteOffLines(detail)
                if (!writeOff) return null
                return (
                  <div className="rounded-lg border border-destructive/40 p-3 text-sm" data-testid="reassignment-write-off">
                    <div className="font-medium">Reassignment written off</div>
                    <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                      <dt className="text-muted-foreground">Moving to</dt>
                      <dd>{writeOff.target}</dd>
                      <dt className="text-muted-foreground">Appointment</dt>
                      <dd>{writeOff.appointment}</dd>
                      <dt className="text-muted-foreground">Written off</dt>
                      <dd>{writeOff.writtenOff}</dd>
                      <dt className="text-muted-foreground">Reason</dt>
                      <dd>{writeOff.reason}</dd>
                    </dl>
                    <div className="mt-1 text-muted-foreground">
                      The Cal booking was cancelled and could not be rebooked. Contact the household and
                      rebook or cancel the appointment with its own actions, then keep it here to
                      acknowledge it.
                    </div>
                  </div>
                )
              })()}

              {/*
                Offered only when there is something to reveal, so a request that never carried a
                number — the ordinary case — shows nothing rather than a dead control. Every press is
                recorded against the staff member who made it.
              */}
              {detail.item.hasSsn && (
                <div className="rounded-lg border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">Social Security number</span>
                    {revealedSsn === null ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={revealing}
                        onClick={() => void revealSsn(detail.item.id)}
                      >
                        {revealing ? <IconLoader2 className="size-4 animate-spin" /> : null}
                        Reveal
                      </Button>
                    ) : (
                      <span className="font-mono">{revealedSsn}</span>
                    )}
                  </div>
                  <div className="text-muted-foreground">
                    {revealedSsn === null
                      ? "Revealing this is recorded against your account."
                      : "This disclosure has been recorded. It clears when you close this dialog."}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {detail.candidates.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No candidate rows were recorded for this request.
                  </div>
                ) : (
                  detail.candidates.map((candidate, index) =>
                    candidate.subject === 0 ? (
                      <ExistingAppointmentCard
                        key={index}
                        candidate={candidate}
                        band={MATCH_BANDS[candidate.band] ?? candidate.band}
                      />
                    ) : (
                      <div key={index} className="rounded-lg border p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Benefit application</span>
                          <Badge variant="outline">{MATCH_BANDS[candidate.band] ?? candidate.band}</Badge>
                        </div>
                        <div className="text-muted-foreground">{candidate.matchingDetails}</div>
                        <div className="text-muted-foreground">Agreed on: {candidate.matchedFields}</div>
                      </div>
                    ),
                  )
                )}
              </div>

              {/* Notes already on the booking, as far as the viewer may see them. */}
              <BookingNotesSection requestId={detail.item.id} />

              <div className="flex flex-col gap-2">
                <Label htmlFor="review-comments">Reviewer comments</Label>
                <Textarea
                  id="review-comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="What you checked and what you decided."
                />
                <p className="text-xs text-muted-foreground">
                  Staff only. Cancelling a duplicate also keeps these as an internal note on the booking.
                </p>
              </div>
            </div>
          )}

          {/* Only the decisions this request accepts: a failed batch move takes Keep alone (AB#809). */}
          <DialogFooter className="gap-2">
            {allowedDecisions(detail).includes(ReviewDecision.Keep) && (
              <Button variant="outline" disabled={saving} onClick={() => void decide(ReviewDecision.Keep)}>
                <IconCheck className="size-4" /> Keep appointment
              </Button>
            )}
            {allowedDecisions(detail).includes(ReviewDecision.CancelDuplicate) && (
              <Button
                variant="destructive"
                disabled={saving}
                onClick={() => void decide(ReviewDecision.CancelDuplicate)}
              >
                {saving ? <IconLoader2 className="size-4 animate-spin" /> : <IconTrash className="size-4" />}
                Cancel duplicate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
